import React from "react";
import styled from "styled-components";

// Types
export interface CalendarDay {
  id: number;
  month: string;
  day: string;
  dayName: string;
  isSelected: boolean;
}

// Styled Components
const CalendarStripContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 12px;
`;

const CalendarDayCard = styled.div<{ isSelected: boolean }>`
  flex: 0 0 auto;
  width: 80px;
  background: #f7f7f7;
  border: 1px solid ${(p) => (p.isSelected ? "#d62422" : "#dedede")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(p) => (p.isSelected ? "#d62422" : "#b0b0b0")};
  }
`;

const CalTop = styled.div`
  height: 28px;
  display: grid;
  place-items: center;
  background-color: #FFFFFF;
  border-bottom: 1px solid #dedede;
`;

const CalMonth = styled.div`
  font-family: Manrope, sans-serif;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #717171;
`;

const CalBody = styled.div`
  height: 72px;
  display: grid;
  place-items: center;
  gap: 2px;
`;

const CalDay = styled.div<{ isSelected: boolean }>`
  font-family: Manrope, sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: ${(p) => (p.isSelected ? "#d62422" : "#2d2d2d")};
`;

const CalDayName = styled.div<{ isSelected: boolean }>`
  font-family: Manrope, sans-serif;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${(p) => (p.isSelected ? "#d62422" : "#2d2d2d")};
`;

const EmptyState = styled.div`
  color: #717171;
  padding: 20px;
  text-align: center;
  font-family: Manrope, sans-serif;
  font-size: 14px;
`;

// Props interface
interface CalendarStripProps {
  days: CalendarDay[];
  onDaySelect: (dayId: number) => void;
  loading?: boolean;
  emptyMessage?: string;
}

// Component
const CalendarStrip: React.FC<CalendarStripProps> = ({
  days,
  onDaySelect,
  loading = false,
  emptyMessage = "No calendar data available"
}) => {
  if (loading) {
    return (
      <CalendarStripContainer>
        <EmptyState>Loading calendar...</EmptyState>
      </CalendarStripContainer>
    );
  }

  if (days.length === 0) {
    return (
      <CalendarStripContainer>
        <EmptyState>{emptyMessage}</EmptyState>
      </CalendarStripContainer>
    );
  }

  return (
    <CalendarStripContainer>
      {days.map((day) => (
        <CalendarDayCard
          key={day.id}
          isSelected={day.isSelected}
          onClick={() => onDaySelect(day.id)}
        >
          <CalTop>
            <CalMonth>{day.month}</CalMonth>
          </CalTop>
          <CalBody>
            <CalDay isSelected={day.isSelected}>{day.day}</CalDay>
            <CalDayName isSelected={day.isSelected}>{day.dayName}</CalDayName>
          </CalBody>
        </CalendarDayCard>
      ))}
    </CalendarStripContainer>
  );
};

export default CalendarStrip;
